<template>
    <div class="vf-login">
        <div class="vf-login__header">
            <img
                class="vf-login__icon"
                :src="ViafouraIcon"
                alt="Viafoura"
            >
            <h1 class="vf-login__title">
                Viafoura Administration
            </h1>
        </div>
        <v-alert
            v-if="serverError"
            type="error"
            closable
            class="vf-login__alert"
            @click:close="serverError = null"
        >
            {{ serverError }}
        </v-alert>
        <ViewContainer class="vf-login__container">
            <v-form
                validate-on="submit"
                @submit.prevent="authenticateUser"
            >
                <div class="vf-login__field">
                    <label class="vf-login__label">Site Domain (URL)</label>
                    <v-text-field
                        ref="domainField"
                        variant="outlined"
                        placeholder="example.com"
                        :rules="[ validateMandatoryField ]"
                        v-model="domain"
                        density="comfortable"
                        hide-details="auto"
                        rounded="md"
                    >
                        <template #prepend-inner>
                            <svg
                                class="vf-login__field-icon"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                            >
                                <path
                                    d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm7.931 9h-2.764a14.67 14.67 0 0 0-1.792-6.243A8.013 8.013 0 0 1 19.931 11zm-6.926-7.764A12.757 12.757 0 0 1 15.011 11H8.989a12.757 12.757 0 0 1 2.006-7.764zM8.625 4.757A14.67 14.67 0 0 0 6.833 11H4.069a8.013 8.013 0 0 1 4.556-6.243zM4.069 13h2.764a14.67 14.67 0 0 0 1.792 6.243A8.013 8.013 0 0 1 4.069 13zm8.926 7.764A12.757 12.757 0 0 1 8.989 13h6.022a12.757 12.757 0 0 1-2.006 7.764h-.01zM15.375 19.243A14.67 14.67 0 0 0 17.167 13h2.764a8.013 8.013 0 0 1-4.556 6.243z"
                                />
                            </svg>
                        </template>
                    </v-text-field>
                </div>
                <div class="vf-login__field">
                    <label class="vf-login__label">Email</label>
                    <v-text-field
                        ref="emailField"
                        variant="outlined"
                        type="email"
                        placeholder="user@example.com"
                        :rules="[ validateMandatoryField, validateEmail ]"
                        v-model="email"
                        density="comfortable"
                        hide-details="auto"
                        rounded="md"
                    >
                        <template #prepend-inner>
                            <svg
                                class="vf-login__field-icon"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                            >
                                <path
                                    d="M20 4H4c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h16c1.103 0 2-.897 2-2V6c0-1.103-.897-2-2-2zm0 2v.511l-8 6.223-8-6.222V6h16zM4 18V9.044l7.386 5.745a.994.994 0 0 0 1.228 0L20 9.044 20.002 18H4z"
                                />
                            </svg>
                        </template>
                    </v-text-field>
                </div>
                <div class="vf-login__field">
                    <label class="vf-login__label">Password</label>
                    <v-text-field
                        ref="passwordField"
                        variant="outlined"
                        type="password"
                        placeholder="Password"
                        :rules="[ validateMandatoryField ]"
                        v-model="password"
                        density="comfortable"
                        hide-details="auto"
                        rounded="md"
                    >
                        <template #prepend-inner>
                            <svg
                                class="vf-login__field-icon"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                            >
                                <path
                                    d="M12 2C9.243 2 7 4.243 7 7v3H6c-1.103 0-2 .897-2 2v8c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2v-8c0-1.103-.897-2-2-2h-1V7c0-2.757-2.243-5-5-5zm6 10l.002 8H6v-8h12zm-9-2V7c0-1.654 1.346-3 3-3s3 1.346 3 3v3H9z"
                                />
                            </svg>
                        </template>
                    </v-text-field>
                </div>
                <div class="vf-login__forgot-password">
                    <a :href="forgotPasswordLink">Forgot password?</a>
                </div>
                <v-btn
                    type="submit"
                    color="primary"
                    variant="flat"
                    block
                    class="vf-login__submit-btn"
                >
                    Login
                </v-btn>
            </v-form>
        </ViewContainer>
        <div class="vf-login__legal">
            <span>&copy; {{ currentYear }}</span>
            <a href="https://viafoura.com/">Viafoura</a>
            <span>·</span>
            <a href="https://viafoura.com/privacy-policy/">Privacy Policy</a>
            <span>·</span>
            <a href="https://documentation.viafoura.com/">Documentation</a>
        </div>
        <DialogMessage
            :is-visible="isDialogMessageVisible"
            :message="dialogMessage"
            :component-message="dialogComponentMessage"
            @close="isDialogMessageVisible = false"
        />
        <DialogLoading
            :is-visible="isLoadingVisible"
        />
    </div>
</template>

<script>
import { shallowRef } from 'vue';
import { mapWritableState, mapActions } from 'pinia';
import config from 'config';
import ViewContainer from '@/components/ViewContainer.vue';
import DialogMessage from '@/components/DialogMessage.vue';
import DialogLoading from '@/components/DialogLoading.vue';
import ErrorGenericMessage from '@/components/ErrorGenericMessage.vue';
import { authorizeUser } from '@/http/endpoint/auth-service.js';
import { forceLogout } from '@/http/endpoint/heimdall.js';
import { createSession } from '@/session/main.js';
import ViafouraIcon from '@/assets/viafoura-icon.svg';
import { useUserStore } from '@/store/user.js';
import { useMfaStore } from '@/store/mfa.js';
import { useParametersStore } from '@/store/parameters.js';
import { validateMandatoryField, validateEmail } from '@/validations/main.js';
import {
    PARAMETER_ERROR_PASSWORD_RESET,
    PARAMETER_ERROR_BAD_STATE,
    API_ERROR_MANDATORY_PARAMETER,
    API_ERROR_CLIENT_NOT_FOUND,
    API_ERROR_INVALID_REDIRECT_URI,
} from '@/constants/main.js';
import debugLog from '@vf/debug-log';

export default {
    components: {
        ViewContainer,
        DialogMessage,
        DialogLoading,
    },
    data: () => ({
        ViafouraIcon,
        isDialogMessageVisible: false,
        dialogMessage: null,
        dialogComponentMessage: null,
        isLoadingVisible: false,
        serverError: null,
        forgotPasswordLink: config.forgotPasswordForm,
    }),
    computed: {
        ...mapWritableState(useUserStore, [ 'domain', 'email', 'password' ]),
        ...mapWritableState(useMfaStore, [ 'isMfaEnabled', 'isMfaConfigured', 'mfaToken', 'mfaSession' ]),
        ...mapWritableState(useParametersStore, [ 'scope', 'clientId', 'redirectUri', 'state', 'error' ]),
        currentYear() {
            return new Date().getFullYear();
        },
    },
    mounted() {
        this.resetUser();

        // Pre-fill domain from last successful login (CM-609)
        try {
            const lastDomain = localStorage.getItem('vf_last_login_domain');
            if (lastDomain) {
                this.domain = lastDomain;
            }
        }
        catch {
            // localStorage may be blocked in some privacy contexts
        }

        this.scope = this.$route.query.scope;
        this.clientId = this.$route.query.client_id;
        this.redirectUri = this.$route.query.redirect_uri;
        this.state = this.$route.query.state;
        this.error = this.$route.query.error;

        if (this.error === PARAMETER_ERROR_PASSWORD_RESET) {
            this.dialogMessage = this.$i18n('errors.temporary_password');
            this.isDialogMessageVisible = true;
        }
        else if (this.error === PARAMETER_ERROR_BAD_STATE) {
            this.dialogComponentMessage = shallowRef(ErrorGenericMessage);
            this.isDialogMessageVisible = true;
        }

        // Set autocomplete attributes directly on inner <input> elements.
        // Vuetify's v-text-field places attributes on the wrapper div,
        // not the actual <input>, so password managers can't see them. (CM-609)
        this.$nextTick(() => {
            const setInputAttrs = (ref, attrs) => {
                const input = this.$refs[ref]?.$el?.querySelector('input');
                if (input) {
                    Object.entries(attrs).forEach(([ key, value ]) => input.setAttribute(key, value));
                }
            };
            setInputAttrs('domainField', {
                name: 'organization',
                autocomplete: 'organization',
            });
            setInputAttrs('emailField', {
                name: 'email',
                autocomplete: 'username',
            });
            setInputAttrs('passwordField', {
                name: 'password',
                autocomplete: 'current-password',
            });
        });
    },
    methods: {
        ...mapActions(useUserStore, { resetUser: 'reset' }),
        validateMandatoryField,
        validateEmail,
        authorizeBodyRequest() {
            return {
                domain: this.domain,
                email: this.email,
                password: this.password,
                scope: this.scope,
                client_id: this.clientId,
                redirect_uri: this.redirectUri,
                state: this.state,
            };
        },
        async authenticateUser(submitEvent) {
            // VForm validates on submit and makes the submit event awaitable for the result.
            const { valid } = await submitEvent;
            if (!valid) {
                return;
            }

            try {
                this.isLoadingVisible = true;
                this.resetDialogMessage();
                this.serverError = null;

                await this.clearCookies();

                const res = await authorizeUser(this.authorizeBodyRequest());
                if (res.data.access_token) {
                    this.startSession(res);
                }
                else if (res.data.mfa_token) {
                    this.redirectToMfaNew(res);
                }
                else if (res.data.mfa_session) {
                    this.redirectToMfaVerify(res);
                }
                else {
                    throw this.$i18n('errors.unexpected');
                }
            }
            catch (exception) {
                if (exception instanceof Response) {
                    this.handleApiException(exception);
                }
                else {
                    this.serverError = this.$i18n('errors.unexpected');
                }
            }
            finally {
                this.isLoadingVisible = false;
            }
        },
        async handleApiException(exception) {
            const res = await exception.json();
            if (
                API_ERROR_MANDATORY_PARAMETER.test(res.error)
                || API_ERROR_INVALID_REDIRECT_URI === res.error
                || API_ERROR_CLIENT_NOT_FOUND === res.error
            ) {
                this.dialogComponentMessage = shallowRef(ErrorGenericMessage);
                this.isDialogMessageVisible = true;
                return;
            }

            this.serverError = res.error;
        },
        startSession(res) {
            // Remember domain for next login (CM-609)
            // Note: MFA path (MfaVerifyView) calls createSession directly
            // and does not go through this method. Domain memory and credential
            // save for MFA users would need equivalent logic there.
            try {
                if (this.domain) {
                    localStorage.setItem('vf_last_login_domain', this.domain);
                }
            }
            catch {
                // localStorage may be blocked or full
            }

            // Prompt browser/password manager to save credentials (CM-609)
            try {
                if (window.PasswordCredential && navigator.credentials?.store) {
                    const cred = new window.PasswordCredential({
                        id: this.email,
                        password: this.password,
                    });
                    navigator.credentials.store(cred).catch(() => {});
                }
            }
            catch {
                // Silently fail — credential storage is best-effort
            }

            createSession({
                accessToken: res.data.access_token,
                accessTokenTtl: res.data.access_token_ttl,
                refreshToken: res.data.refresh_token,
                refreshTokenTtl: res.data.refresh_token_ttl,
                authorizationCode: res.data.code,
                state: this.state,
                redirectUri: this.redirectUri,
                isResetPasswordEntered: res.data.reset_password_entered,
                domainSessionUuid: res.data.section_uuid,
            });
        },
        redirectToMfaNew(res) {
            this.isMfaEnabled = true;
            this.isMfaConfigured = false;
            this.mfaToken = res.data.mfa_token;
            this.mfaSession = res.data.mfa_session;
            this.$router.push({
                name: 'mfa-otp-new',
                query: this.$route.query,
            });
        },
        redirectToMfaVerify(res) {
            this.isMfaEnabled = true;
            this.isMfaConfigured = true;
            this.mfaSession = res.data.mfa_session;
            this.$router.push({
                name: 'mfa-otp-verify',
                query: this.$route.query,
            });
        },
        resetDialogMessage() {
            this.isDialogMessageVisible = false;
            this.dialogMessage = null;
            this.dialogComponentMessage = null;
        },
        async clearCookies() {
            try {
                await forceLogout();
            }
            catch (error) {
                debugLog('Problems on force logout', error);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.vf-login {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;

    &__header {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        margin-bottom: 36px;
    }

    &__icon {
        width: 48px;
        height: 48px;
    }

    &__title {
        font-size: 28px;
        font-weight: 700;
        line-height: 42px;
        color: rgba(34, 48, 62, 0.7);
        text-align: center;
        margin: 0;
    }

    &__alert {
        width: 460px;
        max-width: 100%;
        margin-bottom: 24px;
    }

    &__container {
        width: 460px;
        max-width: 100%;
    }

    &__field {
        margin-bottom: 24px;
    }

    &__label {
        display: block;
        font-size: 13px;
        font-weight: 400;
        line-height: 15px;
        color: rgba(34, 48, 62, 0.9);
        margin-bottom: 4px;
    }

    &__field-icon {
        width: 18px;
        height: 18px;
        color: rgba(34, 48, 62, 0.4);
    }

    &__forgot-password {
        margin-bottom: 24px;

        a {
            font-size: 15px;
            color: #007FFF;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    &__submit-btn {
        text-transform: none;
        font-weight: 500;
        font-size: 15px;
        letter-spacing: 0;
        box-shadow: 0 2px 4px rgba(0, 78, 156, 0.4);
    }

    &__legal {
        margin-top: 24px;
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
        color: rgba(34, 48, 62, 0.5);

        a {
            color: rgba(34, 48, 62, 0.5);
            text-decoration: none;

            &:hover {
                text-decoration: underline;
                color: rgba(34, 48, 62, 0.7);
            }
        }
    }
}
</style>
